/**
 * Google Analytics 4 screen logic, shared by the customer portal (/analytics) and the agency per-client view
 * (partners /clients/:id/analytics). Lives outside routes/ so both surfaces use it without a cross-surface import.
 * Pure helpers + the BFF source. Figures come only from the API (synced data); a value it did not report is null and
 * renders as "Not measured", never 0.
 */
import type { Ga4PropertyView, Ga4Report } from '@qm/contracts/connectors-ga4';
import { bff, type FetchLike } from '../lib/bff';

export type { Ga4PropertyView, Ga4Report };
export type Ga4Preset = '7d' | '28d' | '90d';
export const GA4_PRESETS: readonly Ga4Preset[] = ['7d', '28d', '90d'];
export interface Ga4Range { from: string; to: string }

const DAY = 86_400_000;
const iso = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/** Complete days only: GA4 is synced up to yesterday (UTC), so the range ends yesterday. */
export function ga4RangeFor(preset: Ga4Preset, now: Date): Ga4Range {
  const yesterday = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) - DAY;
  const n = preset === '7d' ? 7 : preset === '28d' ? 28 : 90;
  return { from: iso(yesterday - (n - 1) * DAY), to: iso(yesterday) };
}

export type Ga4Scope = { kind: 'tenant' } | { kind: 'agency'; clientId: string };

export interface Ga4SyncResult { propertyId: string; status: 'succeeded' | 'failed' | 'paused' | 'skipped'; code: string | null; rows: number; pausedUntil?: string | null }

export interface Ga4Source {
  properties(): Promise<Ga4PropertyView[]>;
  report(propertyId: string, r: Ga4Range): Promise<Ga4Report>;
  /** customer owners / admins only; absent in the agency view (read-only) */
  sync?(propertyId: string): Promise<Ga4SyncResult>;
}

export function createGa4Source(scope: Ga4Scope, fetchFn?: FetchLike): Ga4Source {
  const base = scope.kind === 'agency' ? `/v1/connectors/agency/clients/${encodeURIComponent(scope.clientId)}/ga4` as const : '/v1/connectors/ga4' as const;
  const get = <T>(path: string) => bff<T>(`${base}${path}` as `/v1/${string}`, {}, fetchFn);
  return {
    properties: () => get<Ga4PropertyView[]>('/properties'),
    report: (id, r) => get<Ga4Report>(`/properties/${encodeURIComponent(id)}/report?from=${r.from}&to=${r.to}`),
    ...(scope.kind === 'tenant' ? { sync: (id: string) => bff<Ga4SyncResult>(`/v1/connectors/ga4/properties/${encodeURIComponent(id)}/sync`, { method: 'POST', body: '{}' }, fetchFn) } : {}),
  };
}

/** Which notice the report needs, most important first. */
export type Ga4Notice =
  | { kind: 'reconnect'; asOf: string | null }
  | { kind: 'paused'; reason: NonNullable<Ga4PropertyView['pauseReason']>; until: string }
  | { kind: 'missing'; days: number };

export function ga4Notices(r: Ga4Report): Ga4Notice[] {
  const out: Ga4Notice[] = [];
  if (!r.current) out.push({ kind: 'reconnect', asOf: r.asOf });
  if (r.property.pausedUntil && r.property.pauseReason) out.push({ kind: 'paused', reason: r.property.pauseReason, until: r.property.pausedUntil });
  if (r.missingDates.length) out.push({ kind: 'missing', days: r.missingDates.length });
  return out;
}

/** True when nothing was synced for the range (no daily rows at all). */
export const ga4Empty = (r: Ga4Report) => r.daily.length === 0;

/** The property to show: the requested one when it is in the list, else the first. */
export function pickProperty(list: readonly Ga4PropertyView[], wanted: string | null): Ga4PropertyView | null {
  return list.find((p) => p.id === wanted) ?? list[0] ?? null;
}
